import { errorFragment } from "@/gql/GqlErrorFragment";
import { FreightPricingPortalError } from "@/types/error/FreightPricingPortalError";
import { transformApolloErrorToFreightPricingPortalErrors } from "@/lib/error/transformers/TransformApolloErrorToFreightPricingPortalErrors";
import {
  TargetRoutePairInput,
  UpdateRoutePairCustomerTargetInput,
  UpdateRoutePairCustomerTargetMutation,
  UpdateRoutePairCustomerTargetMutationVariables,
} from "@/gql/graphql";
import { FetchResult, gql, useMutation } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { fetchRoutePairCustomerTargetFragment } from "../../features/route-pair-target-table/hooks/UseFetchCustomerRoutePairTargets";

const mutation = gql`
  ${fetchRoutePairCustomerTargetFragment}
  ${errorFragment}
  mutation UpdateRoutePairCustomerTarget(
    $input: UpdateRoutePairCustomerTargetInput!
    $filter: TargetRoutePairInput!
  ) {
    productPrice {
      routePairCustomerTarget {
        updateRoutePairCustomerTargets(input: $input, filter: $filter) {
          __typename
          ... on RoutePairCustomerTargetSuccessResult {
            targetRoutePairs {
              ...FetchRoutePairCustomerTarget
            }
          }
          ... on ErrorResult {
            ...ErrorFragment
          }
        }
      }
    }
  }
`;

interface UseUpdateRoutePairCustomerTargetMutation {
  updateRoutePairCustomerTargets: (
    input: UpdateRoutePairCustomerTargetInput,
    filter: TargetRoutePairInput
  ) => Promise<FetchResult<UpdateRoutePairCustomerTargetMutation>>;
  loading: boolean;
  error: FreightPricingPortalError[] | undefined;
}

export const useUpdateRoutePairCustomerTargetMutation =
  (): UseUpdateRoutePairCustomerTargetMutation => {
    const [mutate, { loading, error }] = useMutation<
      UpdateRoutePairCustomerTargetMutation,
      UpdateRoutePairCustomerTargetMutationVariables
    >(mutation);

    const fppError = useMemo(() => {
      return error && transformApolloErrorToFreightPricingPortalErrors(error);
    }, [error]);

    const updateRoutePairCustomerTargets = useCallback(
      (
        input: UpdateRoutePairCustomerTargetInput,
        filter: TargetRoutePairInput
      ) => {
        return mutate({
          variables: {
            input,
            filter,
          },
          refetchQueries: ["TargetSummary"],
        });
      },
      [mutate]
    );

    return {
      updateRoutePairCustomerTargets,
      loading,
      error: fppError,
    };
  };
